import React, { useState, useCallback, useRef, useEffect } from 'react';
import Toast from './Toast';

interface ToastData {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  undoAction?: () => void;
  undoLabel?: string;
}

interface ToastContextType {
  showToast: (toast: Omit<ToastData, 'id'>) => void;
  showSuccess: (
    message: string,
    duration?: number,
    undoAction?: () => void
  ) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (
    message: string,
    duration?: number,
    undoAction?: () => void
  ) => void;
  showInfo: (message: string, duration?: number) => void;
  clearAll: () => void;
}

const ToastContext = React.createContext<ToastContextType | undefined>(
  undefined
);

export const useToast = () => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: React.ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const toastCounter = useRef(0);
  const maxToasts = 5; // Maximum number of toasts to show at once

  const showToast = useCallback((toast: Omit<ToastData, 'id'>) => {
    const id = `toast-${++toastCounter.current}`;

    setToasts((prev) => {
      const newToasts = [...prev, { ...toast, id }];
      // Remove oldest toasts if we exceed the maximum
      if (newToasts.length > maxToasts) {
        return newToasts.slice(-maxToasts);
      }
      return newToasts;
    });
  }, []);

  const showSuccess = useCallback(
    (message: string, duration?: number, undoAction?: () => void) => {
      showToast({
        message,
        type: 'success',
        duration: duration || 5000,
        undoAction,
        undoLabel: undoAction ? 'Отменить' : undefined,
      });
    },
    [showToast]
  );

  const showError = useCallback(
    (message: string, duration?: number) => {
      showToast({ message, type: 'error', duration: duration || 8000 });
    },
    [showToast]
  );

  const showWarning = useCallback(
    (message: string, duration?: number, undoAction?: () => void) => {
      showToast({
        message,
        type: 'warning',
        duration: duration || 8000,
        undoAction,
        undoLabel: undoAction ? 'Отменить' : undefined,
      });
    },
    [showToast]
  );

  const showInfo = useCallback(
    (message: string, duration?: number) => {
      showToast({ message, type: 'info', duration: duration || 6000 });
    },
    [showToast]
  );

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Auto-adjust positions when toasts are removed
  useEffect(() => {
    if (toasts.length === 0) return;

    const timer = setTimeout(() => {
      // Trigger reposition animation for remaining toasts
      setToasts((prev) => [...prev]);
    }, 50);

    return () => clearTimeout(timer);
  }, [toasts.length]);

  const contextValue: ToastContextType = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    clearAll,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      {/* Toast Container with proper z-index management */}
      <div className="fixed inset-0 pointer-events-none z-50">
        <div className="absolute top-0 right-0">
          {toasts.map((toast, index) => (
            <Toast
              key={toast.id}
              id={toast.id}
              message={toast.message}
              type={toast.type}
              duration={toast.duration}
              onClose={removeToast}
              position={index}
              undoAction={toast.undoAction}
              undoLabel={toast.undoLabel}
            />
          ))}
        </div>

        {/* Clear all button when multiple toasts */}
        {toasts.length > 2 && (
          <div className="absolute top-4 right-4 pointer-events-auto">
            <button
              onClick={clearAll}
              className="
                bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium text-gray-700
                hover:bg-gray-50 hover:border-gray-300 transition-all duration-200
                shadow-sm hover:shadow-md transform hover:scale-105 active:scale-95
                focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:ring-offset-2
              "
            >
              <i className="ri-close-circle-line mr-1"></i>
              Закрыть все
            </button>
          </div>
        )}
      </div>
    </ToastContext.Provider>
  );
}
